import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Clock, Send, Award, XCircle, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { API } from "@/config";
import { useLanguage } from "@/i18n/LanguageContext";
import { formatApiErrorDetail } from "@/utils/apiError";

const fmtClock = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

export default function TakeExam() {
  const { t } = useLanguage();
  const { examId } = useParams();
  const [exam, setExam] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [stage, setStage] = useState("intro");
  const [candidate, setCandidate] = useState({ name: "", email: "", phone: "" });
  const [answers, setAnswers] = useState({});
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const submittedRef = useRef(false);

  useEffect(() => {
    axios
      .get(`${API}/exams/${examId}`)
      .then((r) => setExam(r.data))
      .catch(() => setNotFound(true));
  }, [examId]);

  useEffect(() => {
    if (stage !== "testing") return;
    const interval = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(interval);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [stage]);

  useEffect(() => {
    if (stage === "testing" && secondsLeft === 0 && !submittedRef.current) {
      handleSubmit();
    }
  }, [secondsLeft, stage]);

  const startExam = (e) => {
    e.preventDefault();
    setSecondsLeft(exam.duration_minutes * 60);
    setStage("testing");
  };

  const handleSubmit = async () => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/exams/${examId}/attempt`, {
        ...candidate,
        answers,
      });
      setResult(data);
      setStage("result");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      submittedRef.current = false;
    } finally {
      setSubmitting(false);
    }
  };

  const answeredCount = Object.keys(answers).length;

  return (
    <div data-testid="take-exam-page" className="min-h-screen bg-background text-foreground">
      <Navbar />
      <main className="mx-auto max-w-3xl px-4 py-14 sm:px-6">
        {notFound && (
          <p data-testid="exam-not-found" className="rounded-2xl border border-border bg-card p-8 text-center text-sm text-muted-foreground">
            {t.exam.noExams}
          </p>
        )}

        {!exam && !notFound && (
          <p className="text-sm text-muted-foreground">{t.admin.loading}</p>
        )}

        {exam && stage === "intro" && (
          <div className="rounded-2xl border border-border bg-card p-8 shadow-sm">
            <h1 className="font-heading text-2xl font-bold">{exam.title}</h1>
            <div className="mt-3 flex flex-wrap gap-4 text-sm text-muted-foreground">
              <span>{exam.question_count} {t.exam.questions}</span>
              <span>{exam.duration_minutes} {t.exam.minutes}</span>
              <span>{t.exam.passing} {exam.passing_percent}%</span>
            </div>
            <form onSubmit={startExam} className="mt-8 grid gap-4" data-testid="exam-intro-form">
              <p className="text-sm font-medium">{t.exam.candidateInfo}</p>
              <div className="grid gap-2">
                <Label>{t.enquiry.name} *</Label>
                <Input required value={candidate.name} onChange={(e) => setCandidate({ ...candidate, name: e.target.value })} data-testid="candidate-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label>{t.admin.email} *</Label>
                  <Input required type="email" value={candidate.email} onChange={(e) => setCandidate({ ...candidate, email: e.target.value })} data-testid="candidate-email-input" />
                </div>
                <div className="grid gap-2">
                  <Label>{t.enquiry.phone} *</Label>
                  <Input required value={candidate.phone} onChange={(e) => setCandidate({ ...candidate, phone: e.target.value })} data-testid="candidate-phone-input" />
                </div>
              </div>
              <Button type="submit" className="gap-2" data-testid="exam-begin-button">
                {t.exam.begin} <Send className="h-4 w-4" />
              </Button>
            </form>
          </div>
        )}

        {exam && stage === "testing" && (
          <div>
            <div className="sticky top-16 z-30 flex items-center justify-between rounded-xl border border-border bg-card/95 px-4 py-3 shadow backdrop-blur">
              <h1 className="font-heading text-lg font-bold">{exam.title}</h1>
              <span
                data-testid="exam-timer"
                className={`flex items-center gap-2 rounded-lg px-3 py-1.5 font-mono text-sm font-bold ${
                  secondsLeft < 60 ? "bg-destructive/10 text-destructive" : "bg-muted text-foreground"
                }`}
              >
                <Clock className="h-4 w-4" /> {fmtClock(secondsLeft)}
              </span>
            </div>
            <div className="mt-6 space-y-6">
              {exam.questions.map((q, qi) => (
                <div key={q.id} data-testid={`question-${qi}`} className="rounded-2xl border border-border bg-card p-6 shadow-sm">
                  <p className="font-medium">
                    <span className="text-rose-600">Q{qi + 1}.</span> {q.question}
                  </p>
                  <div className="mt-4 grid gap-2">
                    {q.options.map((opt, oi) => (
                      <button
                        key={`${q.id}-${oi}`}
                        type="button"
                        data-testid={`option-${q.id}-${oi}`}
                        onClick={() => setAnswers({ ...answers, [q.id]: oi })}
                        className={`rounded-lg border px-4 py-2.5 text-left text-sm transition-colors ${
                          answers[q.id] === oi
                            ? "border-rose-600 bg-rose-600/10 font-medium"
                            : "border-border hover:border-rose-500/50"
                        }`}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-8 flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                {answeredCount}/{exam.question_count} {t.exam.questions}
              </p>
              <Button onClick={handleSubmit} disabled={submitting} className="gap-2" data-testid="exam-submit-button">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {t.exam.submit}
              </Button>
            </div>
          </div>
        )}

        {exam && stage === "result" && result && (
          <div data-testid="exam-result" className="rounded-2xl border border-border bg-card p-10 text-center shadow-sm">
            {result.passed ? (
              <Award className="mx-auto h-16 w-16 text-amber-500" />
            ) : (
              <XCircle className="mx-auto h-16 w-16 text-destructive" />
            )}
            <h2 className="mt-4 font-heading text-2xl font-bold">{t.exam.result}</h2>
            <p data-testid="score-text" className="mt-2 text-lg">
              {t.exam.score}: <strong>{result.score}/{result.total}</strong> ({result.percent}%)
            </p>
            <p className={`mt-2 text-sm font-medium ${result.passed ? "text-emerald-600" : "text-destructive"}`}>
              {result.passed ? t.exam.congrats : t.exam.failed}
            </p>
            {result.passed && result.certificate_no && (
              <div className="mt-6">
                <p className="text-xs text-muted-foreground">
                  Certificate No: {result.certificate_no}
                </p>
                <Button
                  className="mt-3 gap-2"
                  data-testid="certificate-download-button"
                  onClick={() => window.open(`${API}/certificates/${result.certificate_no}/pdf`, "_blank")}
                >
                  <CheckCircle2 className="h-4 w-4" /> {t.exam.downloadCert}
                </Button>
              </div>
            )}
            <Button asChild variant="outline" className="mt-6" data-testid="back-to-exams-button">
              <Link to="/exams">{t.exam.backToExams}</Link>
            </Button>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
